import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Key, Copy, Check, RefreshCw, Trash2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { generateApiKey, getCurrentApiKey, revokeApiKey } from '../api/apiKeys';
import Modal from '../components/Modal';
import usePageTitle from '../hooks/usePageTitle';

const METHOD_STYLE = {
  GET:  'bg-blue-50 text-blue-700',
  POST: 'bg-green-50 text-green-700',
};

const ORDER_RESPONSE = `{
    "id": 42,
    "token": "8F3A9C2E",
    "customer": { "id": 7, "name": "Rahim", "phone": "01700000000", ... },
    "status": "PENDING",
    "deliveryAddress": "Mirpur 10, Dhaka",
    "totalAmount": 1760.00,
    "paymentStatus": "UNPAID",
    "paymentMethod": "CASH",
    "deliveryCharge": 60.00,
    "notes": "Call before delivery",
    "items": [
      {
        "id": 101,
        "productId": 1,
        "productTitle": "Classic White Shirt",
        "quantity": 2,
        "unitPrice": 850.00,
        "discountAmount": 0.00,
        "subtotal": 1700.00,
        "variantId": 11,
        "variantCombo": { "size": "M", "color": "White" }
      }
    ],
    "createdAt": "2026-10-22T10:15:30",
    ...
  }`;

const ENDPOINTS = [
  {
    method: 'GET',
    path: '/api/v1/external/categories',
    description: 'All categories with their subcategories (not paginated). Subcategories are identified by name — use it as the subcategory filter on the product list.',
    example: `{
  "success": true,
  "message": "Categories retrieved",
  "data": [
    {
      "id": 3,
      "name": "Shirts",
      "sortOrder": 0,
      "imageUrl": "https://example.com/shirts.jpg",
      "subcategories": [
        { "name": "Formal", "sortOrder": 0, "imageUrl": null },
        { "name": "Casual", "sortOrder": 1, "imageUrl": null }
      ]
    }
  ]
}`,
  },
  {
    method: 'GET',
    path: '/api/v1/external/products',
    description: 'Paginated product list. Query params: page, size (default 50), categoryId, subcategory (name), title. totalStock is the product\'s total across all variants. formattedDesc is optional Markdown (null when not set) — render it with a Markdown library.',
    example: `{
  "success": true,
  "message": "Products retrieved",
  "data": {
    "item_count": 1,
    "page_no": 0,
    "total_pages": 5,
    "items": [
      {
        "id": 1,
        "title": "Classic White Shirt",
        "categoryId": 3,
        "categoryName": "Shirts",
        "subcategoryName": "Formal",
        "description": "100% cotton formal shirt",
        "formattedDesc": "## Features\\n- 100% cotton\\n- Regular fit",
        "price": 850.00,
        "mrp": 1000.00,
        "brand": "Yellow",
        "totalStock": 24,
        "active": true,
        "variants": [
          { "id": 11, "combo": { "size": "M", "color": "White" }, "stockQuantity": 14 },
          { "id": 12, "combo": { "size": "L", "color": "White" }, "stockQuantity": 10 }
        ],
        "images": [
          { "id": 1, "url": "http://localhost:8082/public/photos/abc.jpg", "sortOrder": 0 }
        ],
        "createdAt": "2026-09-01T12:00:00",
        "updatedAt": "2026-10-20T09:30:00"
      }
    ]
  }
}`,
  },
  {
    method: 'GET',
    path: '/api/v1/external/products/{id}',
    description: 'Single product by ID. variants is empty for products without variants. formattedDesc is optional Markdown (null when not set).',
    example: `{
  "success": true,
  "message": "Product retrieved",
  "data": {
    "id": 1,
    "categoryId": 3,
    "categoryName": "Shirts",
    "subcategoryName": "Formal",
    "title": "Classic White Shirt",
    "description": "100% cotton formal shirt",
    "formattedDesc": "## Features\\n- 100% cotton\\n- Regular fit",
    "price": 850.00,
    "mrp": 1000.00,
    "brand": "Yellow",
    "stockQuantity": 24,
    "images": [
      { "id": 1, "url": "http://localhost:8082/public/photos/abc.jpg", "sortOrder": 0 }
    ],
    "variants": [
      { "id": 11, "combo": { "size": "M", "color": "White" }, "stockQuantity": 14 },
      { "id": 12, "combo": { "size": "L", "color": "White" }, "stockQuantity": 10 }
    ]
  }
}`,
  },
  {
    method: 'POST',
    path: '/api/v1/external/orders',
    description: 'Create a customer order (status PENDING). For a product with variants, send variantId from its variants list; leave it out for products without variants. paymentMethod: CASH, BKASH, NAGAD, ROCKET, CARD, BANK_TRANSFER or OTHER. Optional header X-Abuse-Fp: a browser fingerprint used to block abusive repeat orders.',
    example: `// Request body
{
  "customer": {
    "name": "Rahim",
    "phone": "01700000000",
    "email": "rahim@example.com",
    "address": "Mirpur 10, Dhaka"
  },
  "items": [
    { "productId": 1, "quantity": 2, "variantId": 11 }
  ],
  "deliveryAddress": "Mirpur 10, Dhaka",
  "deliveryCharge": 60.00,
  "notes": "Call before delivery",
  "paymentMethod": "CASH",
  "paymentStatus": "UNPAID"
}

// Response (201)
{
  "success": true,
  "message": "Order created",
  "data": ${ORDER_RESPONSE}
}`,
  },
  {
    method: 'GET',
    path: '/api/v1/external/orders/{token}',
    description: 'Look up an order by the token returned when it was created — e.g. for an order-tracking page.',
    example: `{
  "success": true,
  "message": "Order retrieved",
  "data": ${ORDER_RESPONSE}
}`,
  },
];

function EndpointCard({ method, path, description, example }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl bg-white overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
      >
        <span className={`text-xs font-bold px-1.5 py-0.5 rounded font-mono shrink-0 ${METHOD_STYLE[method]}`}>
          {method}
        </span>
        <code className="text-xs font-mono text-gray-700 flex-1">{path}</code>
        {open ? <ChevronUp size={14} className="text-gray-400 shrink-0" /> : <ChevronDown size={14} className="text-gray-400 shrink-0" />}
      </button>
      {open && (
        <div className="border-t border-gray-100 px-4 py-3 space-y-2">
          <p className="text-xs text-gray-500">{description}</p>
          <pre className="text-xs font-mono text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-x-auto whitespace-pre">
            {example}
          </pre>
        </div>
      )}
    </div>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={copy}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
    >
      {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function GeneratedKeyModal({ rawKey, onClose }) {
  return (
    <Modal title="API Key Generated" onClose={onClose}>
      <div className="space-y-4">
        <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <AlertTriangle size={16} className="text-yellow-600 mt-0.5 shrink-0" />
          <p className="text-sm text-yellow-800">
            Copy this key now — it will not be shown again. If you lose it, you will need to regenerate.
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1.5">Your API key</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-mono text-gray-800 break-all select-all">
              {rawKey}
            </code>
            <CopyButton text={rawKey} />
          </div>
        </div>
        <div className="flex justify-end pt-1">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ConfirmModal({ message, onConfirm, onClose, loading }) {
  return (
    <Modal title="Confirm" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-gray-700">{message}</p>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Please wait…' : 'Confirm'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function formatDate(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString();
}

export default function APIKeys() {
  usePageTitle('API Keys');
  const queryClient = useQueryClient();
  const [generatedKey, setGeneratedKey] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const { data: currentKey, isLoading } = useQuery({
    queryKey: ['apiKey'],
    queryFn: () =>
      getCurrentApiKey().then((r) => (r.status === 204 ? null : r.data.data)),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['apiKey'] });

  const generateMutation = useMutation({
    mutationFn: generateApiKey,
    onSuccess: (r) => {
      invalidate();
      setGeneratedKey(r.data.data.rawKey);
      setConfirmAction(null);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: revokeApiKey,
    onSuccess: () => {
      invalidate();
      setConfirmAction(null);
    },
  });

  const handleGenerate = () => {
    if (currentKey) {
      setConfirmAction('regenerate');
    } else {
      generateMutation.mutate();
    }
  };

  const handleRevoke = () => setConfirmAction('revoke');

  const loading = generateMutation.isPending || revokeMutation.isPending;

  return (
    <div className="p-6 max-w-2xl">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">API Keys</h1>
        <p className="text-sm text-gray-500 mt-1">
          Use an API key to access your products and accept orders from an external backend.
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : currentKey ? (
        <div className="border border-gray-200 rounded-xl p-5 bg-white space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <Key size={16} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                <span className="font-mono">{currentKey.keyPrefix}…</span>
              </p>
              <p className="text-xs text-gray-400">Active key</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Created</p>
              <p className="text-gray-700">{formatDate(currentKey.createdAt)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Last used</p>
              <p className="text-gray-700">{formatDate(currentKey.lastUsedAt)}</p>
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              <RefreshCw size={14} />
              Regenerate
            </button>
            <button
              onClick={handleRevoke}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
            >
              <Trash2 size={14} />
              Revoke
            </button>
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center">
          <Key size={24} className="mx-auto text-gray-300 mb-3" />
          <p className="text-sm text-gray-500 mb-4">No active API key. Generate one to get started.</p>
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Generating…' : 'Generate API Key'}
          </button>
        </div>
      )}

      <div className="mt-6 space-y-3">
        <p className="text-xs font-medium text-gray-600">
          External API endpoints — pass the key as{' '}
          <code className="font-mono bg-gray-100 px-1 rounded">X-Api-Key: &lt;key&gt;</code>
        </p>

        {ENDPOINTS.map((ep) => (
          <EndpointCard key={ep.method + ep.path} {...ep} />
        ))}
      </div>

      {generatedKey && (
        <GeneratedKeyModal
          rawKey={generatedKey}
          onClose={() => setGeneratedKey(null)}
        />
      )}

      {confirmAction === 'regenerate' && (
        <ConfirmModal
          message="Regenerating will immediately invalidate your current key. Any backend using the old key will stop working."
          onConfirm={() => generateMutation.mutate()}
          onClose={() => setConfirmAction(null)}
          loading={generateMutation.isPending}
        />
      )}

      {confirmAction === 'revoke' && (
        <ConfirmModal
          message="This will permanently revoke the key. Any backend using it will stop working."
          onConfirm={() => revokeMutation.mutate()}
          onClose={() => setConfirmAction(null)}
          loading={revokeMutation.isPending}
        />
      )}
    </div>
  );
}
